'use client';

import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { getLocalizedPath } from '@/lib/locale-utils';

type Testimonial = {
  id: string;
  name: string;
  role?: string | null;
  content: string;
  image?: string | null;
};

// Fallback images - use empty string to show placeholder instead of 404
const fallbackImages = [
  '', // No fallback - will show placeholder
  '', // No fallback - will show placeholder
  '', // No fallback - will show placeholder
];

function TestimonialCard({ testimonial, isCenter }: { testimonial: Testimonial; isCenter?: boolean }) {
  return (
    <div
      className={`group relative p-8 md:p-10 rounded-lg hover:border-primary-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary-500/20 hover:-translate-y-2 flex-shrink-0 w-full md:w-1/3 flex flex-col ${isCenter
        ? 'md:scale-110 z-10 border-2 border-primary-500 bg-white dark:bg-gray-900'
        : 'md:scale-100 scale-100 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
        }`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/0 via-primary-500/0 to-primary-500/0 group-hover:from-primary-500/10 group-hover:via-primary-500/5 group-hover:to-transparent rounded-lg transition-all duration-500" />
      <div className="relative flex flex-col flex-grow">
        <Quote className="h-8 w-8 text-primary-500/40 mb-4 group-hover:text-primary-400/60 group-hover:scale-110 transition-all duration-300" />
        <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors duration-300 flex-grow">
          &quot;{testimonial.content}&quot;
        </p>
        {testimonial.image ? (
          <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-800 group-hover:border-primary-500/30 transition-colors duration-300 flex items-center gap-4">
            <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-primary-200 dark:border-primary-500/30 group-hover:border-primary-500/60 transition-colors">
              {testimonial.image && testimonial.image.startsWith('/uploads/') ? (
                <Image
                  src={testimonial.image}
                  alt={testimonial.name}
                  fill
                  className="object-cover"
                  loading="lazy"
                  sizes="64px"
                  quality={70}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    // Prevent infinite retries - just hide the image
                    if (!target.dataset.errorHandled) {
                      target.dataset.errorHandled = 'true';
                      target.style.display = 'none';
                    }
                  }}
                />
              ) : testimonial.image && testimonial.image.startsWith('http') ? (
                <Image
                  src={testimonial.image}
                  alt={testimonial.name}
                  fill
                  className="object-cover"
                  loading="lazy"
                  sizes="64px"
                  quality={70}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    // Prevent infinite retries
                    if (!target.dataset.errorHandled) {
                      target.dataset.errorHandled = 'true';
                      target.style.display = 'none';
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-400">
                  No Image
                </div>
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                {testimonial.name}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                {testimonial.role}
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-800 group-hover:border-primary-500/30 transition-colors duration-300">
            <p className="font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
              {testimonial.name}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
              {testimonial.role}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function Testimonials({ initialTestimonials }: { initialTestimonials?: Testimonial[] } = {}) {
  const t = useTranslations('homeTestimonials');
  const locale = useLocale();
  const sectionRef = useScrollAnimation({ threshold: 0.1 });
  const [testimonials, setTestimonials] = useState<Testimonial[]>(() =>
    initialTestimonials?.length
      ? initialTestimonials.slice(0, 6).map((item) => ({ ...item, image: item.image || null }))
      : []
  );
  const [isLoading, setIsLoading] = useState(!initialTestimonials?.length);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const content = useMemo(
    () => ({
      pillLabel: t('pillLabel'),
      title: t('title'),
      description: t('description'),
    }),
    [t]
  );

  useEffect(() => {
    if (initialTestimonials?.length) {
      setTestimonials(
        initialTestimonials.slice(0, 6).map((item) => ({ ...item, image: item.image || null }))
      );
      setIsLoading(false);
      return;
    }
    const fetchTestimonials = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/api/testimonials?locale=${encodeURIComponent(locale)}`, {
          cache: 'no-store',
        });
        if (response.ok) {
          const data = await response.json();
          const fetchedTestimonials = Array.isArray(data) ? data : [];
          const limitedTestimonials = fetchedTestimonials.slice(0, 6);
          const mappedTestimonials = limitedTestimonials.map((item: Testimonial) => ({
            ...item,
            image: item.image || null,
          }));
          setTestimonials(mappedTestimonials);
        } else {
          setTestimonials([]);
        }
      } catch (error) {
        setTestimonials([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, [locale, initialTestimonials?.length]);

  // Max index ensures we don't slide past showing 3 cards
  const maxIndex = Math.max(0, testimonials.length - 3);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(Math.min(Math.max(index, 0), maxIndex));
  }, [maxIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  // Drag handlers
  const handleDragStart = useCallback((clientX: number) => {
    setIsDragging(true);
    setStartX(clientX);
    setCurrentX(clientX);
    setDragOffset(0);
  }, []);

  const handleDragMove = useCallback((clientX: number, startXValue: number) => {
    const diff = clientX - startXValue;
    setCurrentX(clientX);
    setDragOffset(diff);
  }, []);

  const handleDragEnd = useCallback((dragOffsetValue: number) => {
    const threshold = 50; // Minimum drag distance to trigger slide change

    if (Math.abs(dragOffsetValue) > threshold) {
      if (dragOffsetValue > 0) {
        // Dragged right - go to previous
        setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
      } else {
        // Dragged left - go to next
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      }
    }

    setIsDragging(false);
    setDragOffset(0);
  }, [maxIndex]);

  // Global mouse/touch event handlers for smooth dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientX, startX);
    };

    const handleGlobalMouseUp = () => {
      handleDragEnd(dragOffset);
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleDragMove(e.touches[0].clientX, startX);
      }
    };

    const handleGlobalTouchEnd = () => {
      handleDragEnd(dragOffset);
    };

    // Prevent text selection while dragging
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    window.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDragging, startX, dragOffset, handleDragMove, handleDragEnd]);

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  }, [handleDragStart]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    handleDragStart(e.touches[0].clientX);
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    if (startX !== 0) {
      handleDragMove(e.touches[0].clientX, startX);
    }
  }, [isDragging, startX, handleDragMove]);

  // Calculate translation - slide by 1 card (1/3 of container + gap)
  // Only apply translation if there are more than 3 testimonials
  // Add drag offset for smooth dragging
  const baseTranslateX = testimonials.length > 3 ? `calc(-${currentIndex * (100 / 3)}% - ${currentIndex * 1}rem)` : '0';
  const dragTranslateX = isDragging && dragOffset !== 0 ? ` + ${dragOffset}px` : '';
  const translateX = testimonials.length > 3
    ? `calc(${baseTranslateX}${dragTranslateX})`
    : '0';

  return (
    <SectionWrapper ref={sectionRef.ref} variant="gradient" padding="md" className="relative bg-white dark:bg-gray-950">
      {/* Animated background elements */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl animate-pulse" />
      <div
        className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse"
        style={{ animationDelay: '1.5s' }}
      />

      <div className="relative z-10">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
          <SectionHeading
            pill={content.pillLabel}
            title={content.title}
            subtitle={content.description}
            isVisible={sectionRef.isVisible}
          />
        </div>

        {/* Carousel */}
        <div className="relative max-w-7xl mx-auto">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">Loading testimonials...</p>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No testimonials available yet.</p>
            </div>
          ) : (
            <>
              {/* Navigation Arrows */}
              {testimonials.length > 3 && (
                <>
                  <button
                    onClick={goToPrevious}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-20 p-2 rounded-full bg-white shadow border border-gray-200 hover:border-primary-500/50 text-gray-900 hover:text-primary-600 dark:bg-gray-800/80 dark:border-gray-700 dark:text-white transition-all duration-300 hover:scale-110"
                    aria-label="Previous testimonial"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-20 p-2 rounded-full bg-white shadow border border-gray-200 hover:border-primary-500/50 text-gray-900 hover:text-primary-600 dark:bg-gray-800/80 dark:border-gray-700 dark:text-white transition-all duration-300 hover:scale-110"
                    aria-label="Next testimonial"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              {/* Carousel Container */}
              <div className="relative overflow-visible rounded-lg mx-8 md:mx-12 py-8">
                <div
                  className={`flex gap-4 select-none ${isDragging ? 'cursor-grabbing transition-none' : 'transition-transform duration-500 ease-in-out cursor-grab'}`}
                  style={{ transform: `translateX(${translateX})` }}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                >
                  {testimonials.map((testimonial, index) => {
                    // Center card is the one at currentIndex + 1 (middle of 3 visible cards)
                    const isCenter = index === currentIndex + 1;
                    return (
                      <TestimonialCard key={testimonial.id} testimonial={testimonial} isCenter={isCenter} />
                    );
                  })}
                </div>
              </div>

              {/* Dots Indicator */}
              {testimonials.length > 3 && (
                <div className="flex justify-center gap-3 mt-8">
                  {testimonials.map((_, index) => {
                    // Active dot is the center card (currentIndex + 1)
                    const isActive = index === currentIndex + 1;
                    // To center on card at index, set currentIndex to index - 1
                    const targetIndex = Math.max(0, Math.min(index - 1, maxIndex));
                    return (
                      <button
                        key={index}
                        onClick={() => goToSlide(targetIndex)}
                        className="min-w-[48px] min-h-[48px] flex items-center justify-center p-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        aria-label={`Go to slide ${index + 1}`}
                      >
                        <span
                          className={`rounded-full transition-all duration-300 ${isActive ? 'w-8 h-2 bg-primary-500' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600'
                            }`}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Explore more button */}
          <div className="mt-10 flex justify-center">
            <Button
              href={getLocalizedPath('testimonials', locale)}
              variant="primary"
              size="md" className="w-full  sm:w-auto  justify-center"
              showArrow
            >
              {t('exploreMoreButton')}
            </Button>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}
